import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import LegalLinks from '../components/common/LegalLinks';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../hooks/useTheme';
import PasswordInput from '../components/ui/PasswordInput';
import GoogleButton, { AuthDivider } from '../components/ui/GoogleButton';
import BixianLogo from '../assets/logo/BixianLogo.png';
import './Auth.css';

const Login = () => {
  const { login, loginWithGoogle } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const finishLogin = (data) => {
    const { role } = data.user;
    if (role === 'superadmin') navigate('/superadmin/dashboard');
    else if (role === 'owner') navigate('/owner/dashboard');
    else navigate('/dashboard');
    toast.success(`Welcome back, ${data.user.name}!`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      finishLogin(await login(form.email, form.password));
    } catch (err) {
      const msg = err.response?.data?.message || 'Login failed. Please check your credentials.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async (credential) => {
    setLoading(true);
    try {
      finishLogin(await loginWithGoogle(credential));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-nav">
        <div className="auth-brandrow">
          <div className="auth-blogo"><img src={BixianLogo} alt="Bixian" /></div>
          <span className="auth-brand-name">Bixian CRM</span>
        </div>
        <div className="auth-nav-actions">
          <Link to="/pricing" className="auth-navlink">Plans</Link>
          <Link to="/register" className="auth-pill auth-pill-grad">Register</Link>
          <button type="button" className="iconbtn" onClick={toggleTheme} data-tip="Toggle theme">
            {theme === 'dark' ? (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M13.8 3.3a7.2 7.2 0 1 0 3 13.3A8.6 8.6 0 0 1 13.8 3.3Z" /></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="3.6" /><path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.8 4.8l1.4 1.4M13.8 13.8l1.4 1.4M15.2 4.8l-1.4 1.4M5.2 15.2l-1.4-1.4" /></svg>
            )}
          </button>
        </div>
      </div>

      <div className="auth-box">
        <div className="auth-logo">
          <div className="auth-blogo" style={{ width: 56, height: 56, margin: '0 auto 12px' }}><img src={BixianLogo} alt="Bixian" /></div>
          <h1 className="auth-title">Bixian CRM</h1>
          <p className="auth-sub">Maximize your leads, elevate your team, and scale your business to new heights.</p>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Email Address</label>
            <input className="form-control" type="email" placeholder="yourname@gmail.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <PasswordInput className="form-control" placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required autoComplete="current-password" />
          </div>
          <button className={`btn btn-primary${loading ? ' btn-loading' : ''}`} type="submit" disabled={loading} style={{ width: '100%', padding: '12px', fontSize: 14, marginTop: 8, justifyContent: 'center' }}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
          <AuthDivider />
          <GoogleButton text="signin_with" onCredential={handleGoogle} disabled={loading} />
        </div>
        <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13 }}>
          <Link to="/forgot-password" className="auth-link">Forgot your password?</Link>
        </p>
        <p style={{ textAlign: 'center', marginTop: 8, fontSize: 13 }}>
          <Link to="/affiliate/login" className="auth-link">Login as Affiliate</Link>
        </p>
        <LegalLinks action="signing in" style={{ marginTop: 16 }} />
        <p className="auth-footnote">
          Secure multi-tenant CRM platform
        </p>
      </div>
    </div>
  );
};

export default Login;
